"use client";

import { ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const categories = [{
  number: "01",
  title: "GETTING STARTED",
  items: [{
    q: "How do I initiate services with Tiny to Teen Supervised Parenting & Education Services?",
    a: "To begin, please contact our office to discuss your specific needs. We will guide you through our formal intake process, which includes gathering verified court documentation and understanding the unique requirements of your family situation. This process is essential to establish the neutral, safe, and structured environment required for professional monitoring."
  }, {
    q: "What documentation is required for intake?",
    a: "We require a complete copy of the current court order or referral documentation, verified contact information for all parties, and any specific safety protocols or directives mandated by the court. Services cannot commence until all documentation is reviewed and compliance with our agency standards is confirmed."
  }, {
    q: "Is there a consultation fee?",
    a: "We offer an initial intake consultation to review case requirements and explain our professional service structure. Please contact our office directly for current fee schedules, service agreements, and a detailed breakdown of our financial policies and professional boundaries."
  }]
}, {
  number: "02",
  title: "SERVICE EXPECTATIONS",
  items: [{
    q: "What is the role of a supervised parenting time monitor?",
    a: "Our monitors serve as neutral, child-centered observers whose primary responsibility is ensuring the physical and emotional safety of the child. They maintain strict professional boundaries, remaining non-judgmental and objective throughout the session. Monitors are responsible for documenting all interactions according to court-mandated standards and intervening only when safety or program rules are compromised. They do not provide legal advice, therapy, or mediation."
  }, {
    q: "Are your services trauma-informed?",
    a: "Yes. We utilize specific trauma-informed practices designed to minimize secondary stress for children and families navigating court involvement. This includes maintaining a calm, predictable environment, utilizing clear and consistent safety procedures, and ensuring staff remain neutral and respectful at all times. Our approach focuses on creating a sense of security and transparency to support healthy child-parent connections in a structured setting."
  }, {
    q: "Do you provide transportation services?",
    a: "Yes, we offer professional transportation for children and vulnerable individuals throughout Arizona. Our transportation protocols include rigorous safety checks, verified driver credentials, and a commitment to maintaining a neutral, low-stress environment during transitions. This service ensures reliable arrival at supervised parenting time or exchange locations while adhering to all court-ordered safety guidelines and statewide availability considerations."
  }, {
    q: "How are observation reports handled?",
    a: "Our monitors produce comprehensive, objective observation reports that document interactions, behaviors, and adherence to court orders. These reports are handled with the highest level of professional integrity, especially in high-conflict situations, ensuring that all data is factual and free from bias. Reports are released only to authorized legal parties or the court, providing a reliable framework for judicial decision-making and case management."
  }]
}, {
  number: "03",
  title: "SAFETY & COMPLIANCE",
  items: [{
    q: "What safety measures are in place?",
    a: "Safety is our primary concern. We maintain strict, neutral boundaries, conduct thorough intake assessments, and follow established safety protocols for all supervised visits and exchanges. Our staff is trained to intervene immediately if safety or professional boundaries are compromised."
  }, {
    q: "Are your services available statewide?",
    a: "We provide services throughout Arizona. Please contact us to discuss your specific location, travel requirements, and any regional limitations that may apply to your family's specific court-ordered situation."
  }, {
    q: "How do you handle high-conflict situations?",
    a: "We are experienced in navigating high-conflict parenting matters. Our staff is trained to remain neutral, maintain firm professional boundaries, and utilize de-escalation techniques to ensure the focus remains entirely on the child's well-being and safety."
  }]
}];
export default function Faq() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="3466" id="service-questions" className="bg-[#E7D8C7] text-[#2F3631] py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-16">
      <div ref={ref} className={`max-w-7xl mx-auto transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <p className="font-mono text-xs md:text-sm uppercase tracking-widest text-[#8FA27C] mb-8">
                Frequently Asked
              </p>
              <h2 className="font-default font-light text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.05] text-[#2F3631] mb-8">
                Common
                <br />
                <em className="italic font-normal text-[#8FA27C]">Inquiries.</em>
              </h2>
              <p className="font-default text-base md:text-lg leading-relaxed text-[#2F3631]/75 mb-8 max-w-md">
                If you have further questions regarding our services or intake process, please reach out to our team.
              </p>
              <Link to="/ContactPage" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#8FA27C] hover:text-[#5E6A52] transition-colors duration-300 group">
                <span>Contact Us</span>
                <ArrowRight className="size-3 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-8 space-y-16 md:space-y-20">
            {categories.map((category, i) => <div data-index={i} key={category.title}>
                <div className="flex items-baseline gap-4 mb-6">
                  <span className="font-mono text-xs md:text-sm text-[#8FA27C] tracking-widest">
                    {category.number}
                  </span>
                  <h3 className="font-mono text-xs md:text-sm uppercase tracking-widest text-[#8FA27C]">
                    {category.title}
                  </h3>
                </div>

                <Accordion type="single" collapsible className="w-full">
                  {category.items.map((item, j) => <AccordionItem data-index={j} key={j} value={`item-${i}-${j}`} className="border-b border-[#5E6A52]/30 last:border-b-0">
                      <AccordionTrigger className="py-8 hover:no-underline group [&>svg]:hidden">
                        <span className="font-default text-lg md:text-xl text-[#2F3631] text-left pr-6 leading-snug group-hover:text-[#8FA27C] group-data-[state=open]:text-[#8FA27C] transition-colors duration-300">
                          {item.q}
                        </span>
                        <span className="relative size-5 shrink-0 text-[#8FA27C]">
                          <span className="absolute inset-0 flex items-center justify-center font-default text-2xl leading-none transition-all duration-500 group-data-[state=open]:opacity-0 group-data-[state=open]:rotate-90">
                            +
                          </span>
                          <span className="absolute inset-0 flex items-center justify-center font-default text-2xl leading-none opacity-0 -rotate-90 transition-all duration-500 group-data-[state=open]:opacity-100 group-data-[state=open]:rotate-0">
                            ×
                          </span>
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="pb-8 pt-0">
                        <p className="font-default text-base md:text-base text-[#2F3631]/75 leading-[1.8] max-w-2xl">
                          {item.a}
                        </p>
                      </AccordionContent>
                    </AccordionItem>)}
                </Accordion>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}